/**
 * Single source of truth for volume-vs-stack routing AND the user-facing
 * reason that explains the choice. Shared by every viewport host (hanging
 * protocol grid, case-viewer fallback, series picker sort, mode badge).
 *
 * Two signals feed the decision:
 *   - `meta.is_reconstructable` (per-series; false ⇒ stack); the backend
 *     usually stamps a `reconstruction_blocker` code identifying the
 *     specific geometric problem (irregular_spacing, single_instance, ...).
 *   - `verdict` (per-app; "stack-only" ⇒ stack for every series, regardless
 *     of meta). Used by the boot-time GPU probe to route weak-GPU clients
 *     (Intel UHD + ANGLE/D3D11) away from VolumeViewport entirely to avoid
 *     the CONTEXT_LOST_WEBGL / shader-compile-failure cascade.
 *
 * Reason precedence (most specific first):
 *   1. blocker code → user-facing label (per-series geometry problem)
 *   2. stack-only verdict → "GPU not supported"
 *   3. is_reconstructable=false without a code → "Non-volumetric"
 *      (defensive: the backend predicate should always stamp a code, but
 *      we don't depend on that unstated invariant)
 *
 * Co-located so a new stack trigger can't be added to routing without
 * forcing a matching reason label in the same edit.
 *
 * `is_reconstructable` is OHIF-ported (see
 * github.com/OHIF/Viewers/blob/master/platform/core/src/utils/isDisplaySetReconstructable.js)
 * and means "safe for cornerstone ORTHOGRAPHIC + MPR / region-grow", not
 * "renderable at all" — VolumeViewport still scrolls native plane for
 * most blockers. See `is_reconstructable ≠ "can be viewed at all"` note
 * in the project CLAUDE.md.
 */
import { SeriesMetadata } from "./dicomMetadataLoader";
import type { GpuVerdict } from "./gpuVerdict";
import { BLOCKER_LABELS, type BlockerCode } from "./reconstructionBlocker";

export type ViewportKind = "volume" | "stack";

export interface ViewportDecision {
  kind: ViewportKind;
  /** User-facing label for why stack mode was chosen, or null when
   *  kind === "volume". */
  reason: string | null;
}

export function decideViewport(
  meta: SeriesMetadata | null | undefined,
  verdict: GpuVerdict | undefined = "volume-ok",
): ViewportDecision {
  const blockerCode = (meta?.reconstruction_blocker ?? null) as BlockerCode | null;

  if (blockerCode) {
    return { kind: "stack", reason: BLOCKER_LABELS[blockerCode] ?? "Non-volumetric" };
  }
  if (verdict === "stack-only") {
    return { kind: "stack", reason: "GPU not supported" };
  }
  if (meta?.is_reconstructable === false) {
    return { kind: "stack", reason: "Non-volumetric" };
  }
  return { kind: "volume", reason: null };
}
